import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';

import * as AppPropTypes from '../../lib/PropTypes';

import { useSetUser, useAuth } from '../../contexts/AuthContext';
import { useUrlGenerator } from '../../contexts/RoutesContext';
import PillButton from '../buttons/Pill';
import NewsletterForm from '../forms/Newsletter';
import CogIcon from '../icons/Cog';
import CrossIcon from '../icons/Cross';
import DotsIcon from '../icons/Dots';
import ExitIcon from '../icons/Exit';
import IconLink from './IconLink';

import styles from '../../../styles/partials/user-sidebar.module.scss';

const propTypes = {
    user: AppPropTypes.user,
    showDelete: PropTypes.bool,
    className: PropTypes.string,
};

const defaultProps = {
    user: null,
    showDelete: false,
    className: null,
};

const UserSidebar = ({ user = null, showDelete = false, className }) => {
    const route = useUrlGenerator();
    const history = useHistory();

    const { logout } = useAuth();
    const onLogout = useCallback(() => {
        logout();
        history.push(route('home'));
    }, [history, route, logout]);

    const setUser = useSetUser();
    const onUserSaved = useCallback(
        (value) => {
            setUser(value);
        },
        [setUser],
    );

    const [optionsToggled, setOptionsToggled] = useState(false);
    const toggleProfileOptions = useCallback(() => {
        setOptionsToggled(!optionsToggled);
    }, [optionsToggled]);

    return (
        <div
            className={classNames(styles.container, {
                [className]: className !== null,
                [styles.optionsToggled]: optionsToggled === true,
            })}
        >
            <aside className={styles.sidebar}>
                {user !== null ? (
                    <header className={styles.sidebarHeader}>
                        <h2 className={styles.heading}>{user.name}</h2>
                    </header>
                ) : null}
                <PillButton
                    className={styles.showProfileMenuButton}
                    onClick={toggleProfileOptions}
                    icon={<DotsIcon direction={optionsToggled ? 'horizontal' : 'vertical'} />}
                    title={
                        <FormattedMessage
                            defaultMessage="edit profile"
                            description="Button label"
                        />
                    }
                />
                <div className={styles.profileOptions}>
                    <p>
                        <FormattedMessage
                            defaultMessage="This space is your own. In it, you will find your purchases and favorited items."
                            description="Account description"
                        />
                    </p>
                    <NewsletterForm className={styles.checkbox} onComplete={onUserSaved} />
                    {showDelete ? (
                        <IconLink
                            className={classNames([styles.button, styles.danger])}
                            icon={<CrossIcon />}
                            href={route('account.delete')}
                            isDark
                        >
                            <FormattedMessage
                                defaultMessage="Delete profile"
                                description="Button label"
                            />
                        </IconLink>
                    ) : (
                        <IconLink
                            className={styles.button}
                            icon={<CogIcon />}
                            href={route('account.profile')}
                            isDark
                        >
                            <FormattedMessage
                                defaultMessage="Edit profile"
                                description="Button label"
                            />
                        </IconLink>
                    )}
                    <IconLink
                        className={styles.button}
                        icon={<ExitIcon />}
                        onClick={onLogout}
                        external
                        target="_self"
                        isDark
                    >
                        <FormattedMessage defaultMessage="Log out" description="Button label" />
                    </IconLink>
                </div>
            </aside>
        </div>
    );
};

UserSidebar.propTypes = propTypes;
UserSidebar.defaultProps = defaultProps;

export default UserSidebar;
